import type { FC, ReactNode } from 'react';

import { cn } from '@/shared/libs';
import { CountryFlag } from '@/shared/ui';

import { REGION_META, regionLabelToCode } from '../../libs/regions';
import { RegionGlyph } from '../RegionGlyph/RegionGlyph';

export interface GeoBadgeProps {
  className?: string;
  countries: string[];
  prominent?: boolean;
  regions: string[];
}

interface GeoItem {
  icon: ReactNode;
  key: string;
  text: string;
}

export const GeoBadge: FC<GeoBadgeProps> = ({
  className,
  countries,
  prominent = false,
  regions,
}) => {
  const codes = regions.flatMap((region) => {
    const code = regionLabelToCode(region);

    return code && code !== 'GLOBAL' ? [code] : [];
  });
  const global = regions.some(
    (region) => regionLabelToCode(region) === 'GLOBAL',
  );
  const items: GeoItem[] = global
    ? [
        {
          icon: <RegionGlyph code="GLOBAL" />,
          key: 'GLOBAL',
          text: REGION_META.GLOBAL.abbrev,
        },
      ]
    : [
        ...codes.map((code) => ({
          icon: <RegionGlyph code={code} />,
          key: code,
          text: REGION_META[code].abbrev,
        })),
        ...countries
          .filter((country) => country.length > 0)
          .slice(0, 3)
          .map((country) => ({
            icon: <CountryFlag code={country} />,
            key: country,
            text: country,
          })),
      ];
  const visible = items.slice(0, prominent ? 2 : 3);

  if (visible.length === 0) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }

  return (
    <span
      className={cn(
        'inline-flex flex-wrap items-center gap-1',
        prominent && 'text-sm font-bold tracking-tight uppercase',
        className,
      )}>
      {visible.map((item) => (
        <span key={item.key} className="inline-flex items-center gap-1">
          {item.icon}
          {item.text}
        </span>
      ))}
      {items.length > visible.length && (
        <span className="text-[11px] font-medium text-muted-foreground normal-case">
          +{items.length - visible.length}
        </span>
      )}
    </span>
  );
};

GeoBadge.displayName = 'GeoBadge';
